import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { armazenamento } from './armazenamento.ts';
import { diaEmLuanda, diasEntre, sequencia } from './dataLuanda.ts';

/**
 * RF-040 / RNF-014 / H-051 — os pontos são um registo de eventos.
 *
 * O total **nunca** é guardado como valor mutável: é sempre derivado da soma
 * dos eventos. Isso resolve três coisas de uma vez — o total pode ser
 * recalculado no servidor a partir da origem, dois dispositivos que sincronizem
 * não somam duas vezes, e um erro de contagem corrige-se sem perder o histórico.
 *
 * Cada evento é único por `(tipo, origem)`. É a mesma restrição que a base de
 * dados impõe em `points_events`, e é o que torna a sincronização idempotente:
 * o mesmo exercício nunca paga duas vezes, venha por onde vier.
 */

export type TipoDeEvento = 'exercicio' | 'licao' | 'dica';

export interface Evento {
  tipo: TipoDeEvento;
  /** Identificador do exercício ou da lição. Único dentro do tipo. */
  origem: string;
  /** Positivo ganha pontos, negativo gasta (RN-007: a segunda dica custa). */
  quantia: number;
  criadoEm: number;
  /** Dia civil em Luanda, congelado no momento do registo (RN-002). */
  dia: string;
  /**
   * A marca de «já foi» da sincronização (ADR 0009, bloco 3). Ausente até o
   * servidor confirmar o lote. É a única memória de sincronização que existe:
   * reenviar é gratuito, portanto perder uma resposta custa um reenvio.
   */
  enviado?: boolean;
}

interface Estado {
  eventos: Evento[];
  /**
   * Último dia com actividade registada. Serve de trinco contra recuar o
   * relógio: um evento datado antes deste é aceite pelos pontos mas não conta como
   * dia novo para a sequência.
   */
  ultimoDia: string | null;
  objectivoDiarioMinutos: 5 | 10 | 20;
  /**
   * Segundos de estudo por dia civil de Luanda. É o que mede o objectivo
   * diário, e é a única coisa neste ficheiro que não é derivável dos eventos —
   * o tempo não deixa rasto nos pontos.
   *
   * Fica local e não sincroniza: é uma medida do hábito de quem está a estudar
   * neste aparelho, não um saldo que dois dispositivos tenham de conciliar. Se
   * um dia contar para alguma coisa que o servidor decida, passa a evento.
   */
  tempoPorDia: Record<string, number>;

  registar(tipo: TipoDeEvento, origem: string, quantia: number): void;
  marcarEnviados(chaves: readonly { tipo: TipoDeEvento; origem: string }[]): void;
  definirObjectivo(minutos: 5 | 10 | 20): void;
  /** Acrescenta tempo de estudo ao dia de hoje. Chamado pelo relógio da lição. */
  registarTempo(segundos: number): void;
  limpar(): void;
}

export const useEventos = create<Estado>()(
  persist(
    (set, get) => ({
      eventos: [],
      ultimoDia: null,
      objectivoDiarioMinutos: 10,
      tempoPorDia: {},

      registar(tipo, origem, quantia) {
        const estado = get();
        // Idempotência: a mesma origem, no mesmo tipo, só entra uma vez.
        if (estado.eventos.some((e) => e.tipo === tipo && e.origem === origem)) return;

        const agora = Date.now();
        const dia = diaEmLuanda(agora);

        // RN-002 — adiantar o relógio não pode inventar dias de sequência.
        // Um dia anterior ao último registado é sinal de relógio recuado: os pontos
        // conta na mesma, mas o dia guardado não regride.
        const recuou = estado.ultimoDia !== null && diasEntre(estado.ultimoDia, dia) < 0;
        const diaAContar = recuou ? estado.ultimoDia! : dia;

        set({
          eventos: [...estado.eventos, { tipo, origem, quantia, criadoEm: agora, dia: diaAContar }],
          ultimoDia: diaAContar,
        });
      },

      marcarEnviados(chaves) {
        if (chaves.length === 0) return;
        set({
          eventos: get().eventos.map((e) =>
            chaves.some((c) => c.tipo === e.tipo && c.origem === e.origem)
              ? { ...e, enviado: true }
              : e,
          ),
        });
      },

      definirObjectivo(minutos) {
        set({ objectivoDiarioMinutos: minutos });
      },

      registarTempo(segundos) {
        if (segundos <= 0) return;
        const estado = get();
        const hoje = diaEmLuanda();
        // O mesmo trinco dos pontos (RN-002): recuar o relógio do aparelho não
        // abre um dia novo para trás. Aqui a consequência de não haver trinco
        // seria mais discreta e igualmente errada — o objectivo de um dia já
        // fechado voltaria a ficar por cumprir.
        const recuou = estado.ultimoDia !== null && diasEntre(estado.ultimoDia, hoje) < 0;
        const dia = recuou ? estado.ultimoDia! : hoje;
        set({
          tempoPorDia: { ...estado.tempoPorDia, [dia]: (estado.tempoPorDia[dia] ?? 0) + segundos },
        });
      },

      limpar() {
        set({ eventos: [], ultimoDia: null, tempoPorDia: {} });
      },
    }),
    { name: 'eventos-pontos', storage: armazenamento() },
  ),
);

// ---------------------------------------------------------------------------
// Derivações. Nenhuma destas é guardada.
// ---------------------------------------------------------------------------

export function pontosTotal(eventos: readonly Evento[]): number {
  return eventos.reduce((soma, e) => soma + e.quantia, 0);
}

export function pontosDaLicao(eventos: readonly Evento[], licaoId: string): number {
  return eventos
    .filter((e) => e.origem === licaoId || e.origem.startsWith(`${licaoId}-`))
    .reduce((soma, e) => soma + e.quantia, 0);
}

export function pontosDeHoje(eventos: readonly Evento[], hoje = diaEmLuanda()): number {
  return eventos.filter((e) => e.dia === hoje).reduce((soma, e) => soma + Math.max(e.quantia, 0), 0);
}

export function sequenciaActual(eventos: readonly Evento[], hoje = diaEmLuanda()): number {
  return sequencia(
    eventos.filter((e) => e.quantia > 0).map((e) => e.dia),
    hoje,
  );
}

/**
 * O passo do relógio de estudo, em segundos.
 *
 * O tempo conta-se aos bocados e não de uma vez, porque o que interessa medir é
 * o tempo **com a aplicação à frente**. Um separador esquecido aberto de um dia
 * para o outro cumpria o objectivo diário sem ninguém ter estudado nada, e um
 * objectivo que se cumpre sozinho deixa de significar o que quer que seja.
 *
 * Cinco segundos é curto para o erro de arredondamento não contar e longo para
 * não acordar o telefone à toa — o que num telemóvel de gama média com dados
 * pré-pagos não é detalhe.
 */
export const PASSO_DO_RELOGIO_S = 5;

// RN-007 (o custo da segunda dica) e o bónus de lição viviam aqui como
// constantes até 16/08/2026. Passaram a configuração publicada com o conteúdo
// (H-207, ADR 0023) — ver `state/economia.ts`, que guarda as omissões com os
// mesmos valores e o porquê de serem pequenos.

/**
 * Progresso no objectivo diário (RF-042), em **minutos verdadeiros**.
 *
 * Até 15/08/2026 isto era uma conversão: o objectivo era em minutos, o que se
 * registava eram pontos, e no meio estava um `PONTOS_POR_MINUTO = 10` deduzido
 * do tempo que os autores declaravam nos exercícios. Media a produtividade e
 * chamava-lhe tempo — quem resolvesse depressa cumpria o objectivo sem lá ter
 * estado os minutos que escolheu, e quem hesitasse não o cumpria nunca.
 *
 * Agora o tempo é o tempo. O relógio corre enquanto a lição está aberta e à
 * vista, e o objectivo compara segundos com segundos. É também o que permitiu
 * ao autor deixar de declarar a duração de cada exercício: a única noção de
 * tempo que o produto tem é a que o estudante escolhe para si.
 *
 * Devolve segundos nos dois lados, para a barra não ter de arredondar duas
 * vezes o mesmo número.
 */
export function progressoDiario(
  tempoPorDia: Readonly<Record<string, number>>,
  objectivoMinutos: number,
  hoje = diaEmLuanda(),
): { feito: number; alvo: number; cumprido: boolean } {
  const alvo = objectivoMinutos * 60;
  const feito = tempoPorDia[hoje] ?? 0;
  return { feito, alvo, cumprido: feito >= alvo };
}
